import React, { useState } from 'react';
import styled from 'styled-components';
import { DateSelect } from 'components/FormFields';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const DateSelectDocs = () => {
  const [basicDate, setBasicDate] = useState('');
  const [prefilledDate, setPrefilledDate] = useState('06-15-2025');
  const [modeDate, setModeDate] = useState('');

  const props = [
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label text displayed above the date select',
    },
    {
      name: 'required',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows a red asterisk next to the label',
    },
    {
      name: 'value',
      type: 'string',
      required: false,
      description: 'Date value in MM-DD-YYYY format',
    },
    {
      name: 'onChange',
      type: 'function',
      required: true,
      description:
        'Callback when date changes. Receives date string in MM-DD-YYYY format.',
    },
    {
      name: 'helperText',
      type: 'string',
      required: false,
      description: 'Helper or error text displayed below the selects',
    },
    {
      name: 'error',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows error state styling on all three dropdowns',
    },
    {
      name: 'startYear',
      type: 'number',
      required: false,
      description: 'First year in the year dropdown (default: current year)',
    },
    {
      name: 'endYear',
      type: 'number',
      required: false,
      description: 'Last year in the year dropdown (default: current year + 5)',
    },
    {
      name: 'width',
      type: 'string',
      default: '"100%"',
      required: false,
      description: 'Width of the container',
    },
    {
      name: 'maxWidth',
      type: 'string',
      required: false,
      description: 'Maximum width of the container',
    },
    {
      name: 'showModeToggle',
      type: 'boolean',
      default: 'false',
      required: false,
      description:
        'Shows a toggle button to switch between dropdown and calendar picker modes',
    },
  ];

  return (
    <>
      <PageTitle>DateSelect</PageTitle>
      <PageDescription>
        A date picker using three dropdown selects for month, day, and year.
        Handles leap years, validates day limits per month, and optionally
        supports a calendar mode toggle. Values use MM-DD-YYYY format.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Date Select</SectionTitle>
        <ComponentDemo label="Three-dropdown date picker" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <DateSelect
              label="Event Date"
              required
              value={basicDate}
              onChange={setBasicDate}
              placeholder="Select date"
            />
            {basicDate && (
              <SelectedValue>
                Selected: <code>{basicDate}</code>
              </SelectedValue>
            )}
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { DateSelect } from 'components/FormFields';

const [date, setDate] = useState('');

<DateSelect
  label="Event Date"
  required
  value={date}
  onChange={setDate}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Pre-filled Value</SectionTitle>
        <ComponentDemo label="Date with pre-filled value" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <DateSelect
              label="Start Date"
              value={prefilledDate}
              onChange={setPrefilledDate}
              startYear={2024}
              endYear={2030}
            />
            <SelectedValue>
              Value: <code>{prefilledDate}</code>
            </SelectedValue>
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>With Error State</SectionTitle>
        <ComponentDemo
          label="Date select with validation error"
          align="stretch"
        >
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <DateSelect
              label="Birth Date"
              required
              value=""
              onChange={() => undefined}
              error
              helperText="Birth date is required"
              startYear={1940}
              endYear={2010}
            />
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Calendar Mode Toggle</SectionTitle>
        <ComponentDemo
          label="Switch between dropdowns and calendar"
          align="stretch"
        >
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <DateSelect
              label="Preferred Date"
              value={modeDate}
              onChange={setModeDate}
              showModeToggle
            />
            {modeDate && (
              <SelectedValue>
                Selected: <code>{modeDate}</code>
              </SelectedValue>
            )}
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`// With calendar mode toggle
<DateSelect
  label="Preferred Date"
  value={date}
  onChange={setDate}
  showModeToggle
/>

// Custom year range
<DateSelect
  label="Birth Date"
  value={birthDate}
  onChange={setBirthDate}
  startYear={1940}
  endYear={2010}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock>
          {`import { DateSelect } from 'components/FormFields';
// or
import DateSelect from 'components/FormFields/DateSelect';`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>Three-dropdown layout for month, day, and year</li>
          <li>
            Automatic day validation based on month and year (handles leap
            years)
          </li>
          <li>Optional calendar mode toggle for alternative date picking</li>
          <li>Custom year range support</li>
          <li>Theme-aware (light/dark mode)</li>
          <li>Error state and helper text support</li>
        </ul>
      </Section>
    </>
  );
};

export default DateSelectDocs;
